import React, { useEffect } from 'react';
import { CheckCircle, Award, Users, Target, TrendingUp, Globe, Briefcase } from 'lucide-react';

const AboutPage = ({ onGetStarted }) => {
  useEffect(() => {
    document.title = 'About Us | Federal IP Office - Strategic Legal Partners';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about Federal IP Office, a full-service law firm providing strategic legal solutions for businesses. Decades of experience in corporate law, IP protection, and compliance.');
    }
  }, []);
  const values = [
    {
      title: 'Excellence',
      description: 'We maintain the highest standards of legal practice and client service in every matter we handle.',
    },
    {
      title: 'Integrity',
      description: 'Our commitment to ethical practice and transparency builds lasting trust with our clients.',
    },
    {
      title: 'Innovation',
      description: 'We leverage cutting-edge legal strategies and technology to deliver superior results.',
    },
    {
      title: 'Client Focus',
      description: 'Your business objectives drive our legal strategies and recommendations.',
    },
  ];

  const achievements = [
    { title: 'Years of Experience', description: 'Decades of combined legal expertise' },
    { title: 'Clients Served', description: 'Businesses nationwide trust our counsel' },
    { title: 'Cases Handled', description: 'Extensive litigation and transactional experience' },
    { title: 'Success Rate', description: 'Proven track record of favorable outcomes' },
  ];

  const industries = [
    'Technology & Software',
    'Healthcare & Biotech',
    'Financial Services',
    'Manufacturing',
    'Real Estate',
    'E-commerce & Retail',
    'Media & Entertainment',
    'Non-Profit Organizations',
  ];

  const recognitions = [
    {
      icon: Award,
      title: 'Top Legal Firm 2024',
      org: 'National Law Journal',
    },
    {
      icon: Award,
      title: 'Best Corporate Law Practice',
      org: 'Legal Excellence Awards',
    },
    {
      icon: Award,
      title: 'Client Satisfaction Award',
      org: 'American Bar Association',
    },
    {
      icon: Award,
      title: 'IP Law Firm of the Year',
      org: 'Intellectual Property Magazine',
    },
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5673488/pexels-photo-5673488.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/75"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">
              About Us
            </div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6" data-testid="about-heading">
              Strategic Legal Partners for Your Business Success
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Federal IP Office is a full-service law firm dedicated to providing strategic legal solutions for businesses of all sizes. Our team combines deep legal expertise with practical business insight to protect what matters most to our clients.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/f237e9eb-e331-43f7-8564-6c8a8f9e5061/images/950ee7723d412c9c3dff262dfba183f613f827d4c8afd6f8b57f0da3ceeea98a.png"
                alt="Legal expertise"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">
                Our Mission
              </h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">
                We are committed to delivering exceptional legal services that empower businesses to grow, innovate, and succeed. Our mission is to provide strategic counsel that not only addresses immediate legal needs but also anticipates future challenges and opportunities.
              </p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-8">
                Through personalized attention, innovative solutions, and unwavering dedication, we build long-term partnerships with our clients, serving as trusted advisors throughout their business journey.
              </p>
              <button
                onClick={onGetStarted}
                className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium"
                data-testid="about-cta-button"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">
              What We Stand For
            </div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">
              Our Core Values
            </h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">
              These principles guide every decision we make and every relationship we build.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 hover:bg-white/10 transition-all duration-300 group" data-testid={`value-${index}`}>
                <CheckCircle className="w-10 h-10 text-[#CA9A67] mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="text-xl text-white font-semibold mb-3">{value.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-24 md:py-32 bg-[#2C3E50]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center" data-testid={`achievement-${index}`}>
                <div className="text-3xl lg:text-4xl text-white font-bold mb-3">
                  {achievement.title}
                </div>
                <div className="text-white/80 text-base">
                  {achievement.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">
              Industries We Serve
            </h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">
              Our legal expertise spans across multiple industries, providing specialized counsel tailored to each sector's unique challenges.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-[#F8F9FA] border border-[#2C3E50]/5 p-6 text-center hover:border-[#2C3E50]/20 transition-all"
              >
                <Briefcase className="w-8 h-8 text-[#CA9A67] mx-auto mb-3" strokeWidth={1.5} />
                <div className="text-[#2E2E2E] font-medium">{industry}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">
              Awards & Recognition
            </h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">
              Our commitment to excellence has been recognized by leading legal organizations and publications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {recognitions.map((recognition, index) => {
              const Icon = recognition.icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-[#2C3E50]/5 shadow-sm p-8 text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#CA9A67] mb-6">
                    <Icon className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{recognition.title}</h3>
                  <p className="text-sm text-[#2E2E2E]/60">{recognition.org}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-12 text-center">
              Why Choose Federal IP Office
            </h2>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#CA9A67] flex items-center justify-center text-white font-semibold">
                  1
                </div>
                <div>
                  <h3 className="text-xl text-[#2C3E50] font-semibold mb-2">
                    Proven Track Record
                  </h3>
                  <p className="text-base leading-relaxed text-[#2E2E2E]/80">
                    With hundreds of successful cases and satisfied clients, we have demonstrated our ability to deliver results across a wide range of legal matters.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#CA9A67] flex items-center justify-center text-white font-semibold">
                  2
                </div>
                <div>
                  <h3 className="text-xl text-[#2C3E50] font-semibold mb-2">
                    Comprehensive Expertise
                  </h3>
                  <p className="text-base leading-relaxed text-[#2E2E2E]/80">
                    Our attorneys bring specialized knowledge in corporate law, intellectual property, compliance, and litigation, providing comprehensive legal support under one roof.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#CA9A67] flex items-center justify-center text-white font-semibold">
                  3
                </div>
                <div>
                  <h3 className="text-xl text-[#2C3E50] font-semibold mb-2">
                    Client-Centered Approach
                  </h3>
                  <p className="text-base leading-relaxed text-[#2E2E2E]/80">
                    We take time to understand your unique business objectives and tailor our legal strategies to align with your goals, ensuring practical and effective solutions.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#CA9A67] flex items-center justify-center text-white font-semibold">
                  4
                </div>
                <div>
                  <h3 className="text-xl text-[#2C3E50] font-semibold mb-2">
                    Responsive Communication
                  </h3>
                  <p className="text-base leading-relaxed text-[#2E2E2E]/80">
                    We believe in keeping our clients informed every step of the way, providing clear explanations and prompt responses to your questions and concerns.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
